/**
 * The curated half of the subdivision dataset: WHICH subdivisions the game
 * asks for, in what order, and how each one is described when the prompt does
 * not simply name it.
 *
 * Everything else about a subdivision — outline, centroid, bounding box, area,
 * capital, major cities — is derived in `scripts/bakestates.ts` from
 * `us-atlas` and GeoNames. Only these two things cannot be: fame is a
 * judgement, and a clue is a sentence.
 *
 * PURE ON PURPOSE. This module imports nothing, least of all the generated
 * JSON, because the bake reads it to decide which subdivisions to emit cities
 * for — and a bake that depends on its own output cannot run the first time.
 *
 * WHAT A CLUE MAY NOT CONTAIN
 * ---------------------------
 *   1. The subdivision's own name, or a word that spells it (the river for
 *      Mississippi, the bay for Delaware). Rung 1 would answer itself.
 *   2. Its CAPITAL, or a landmark that stands in the capital. Rung 2 asks
 *      which of four dots is the capital, and a clue that says "a 500-mile
 *      motor race" has already pointed at Indianapolis.
 *
 * The second rule is why Indiana is described by limestone and Alabama by
 * Huntsville: the obvious clue for each named the capital.
 */

export interface CuratedSubdivision {
  /** ISO 3166-2. Joins to the baked geometry and city tables. */
  readonly code: string;
  /** A unique, stable clue that names neither the subdivision nor its capital. */
  readonly clue: string;
}

/**
 * Fame order — most recognisable first, and the ONLY thing that decides tier
 * pools, which are prefixes of this list. Widening a tier therefore only ever
 * adds subdivisions a player has not been asked for, and never reshuffles the
 * ones they have learned.
 *
 * "Recognisable" here means recognisable ON A MAP WITH NO LABELS, which is not
 * the same as populous: Nevada and Utah rank above Pennsylvania because their
 * outlines are unmistakable, and New Jersey ranks below Maine for the opposite
 * reason.
 */
export const CURATED_SUBDIVISIONS: readonly CuratedSubdivision[] = [
  { code: 'US-CA', clue: 'The Golden Gate Bridge, Yosemite and Death Valley all lie in this Pacific state.' },
  { code: 'US-TX', clue: 'The Alamo stands in the second-largest state, which borders four Mexican states.' },
  { code: 'US-FL', clue: 'Cape Canaveral, the Everglades and a long chain of keys belong to this peninsula.' },
  { code: 'US-NY', clue: 'Niagara Falls sits at one end of this state and the Statue of Liberty at the other.' },
  { code: 'US-AK', clue: 'The tallest peak in North America rises in this state, cut off from the rest by Canada.' },
  { code: 'US-HI', clue: 'Pearl Harbor and an active shield volcano lie in this Pacific archipelago state.' },
  { code: 'US-NV', clue: 'Hoover Dam, Area 51 and a desert casino strip belong to this Great Basin state.' },
  { code: 'US-AZ', clue: 'The Grand Canyon and the saguaro cactus mark this Sonoran Desert state.' },
  { code: 'US-CO', clue: 'This rectangular state holds the highest paved passes in the Rocky Mountains.' },
  { code: 'US-WA', clue: 'Mount Rainier and Puget Sound sit in this corner state facing British Columbia.' },
  { code: 'US-IL', clue: 'A Great Lake laps the north-east corner of this prairie state, whose southern tip reaches the Ohio River.' },
  { code: 'US-MI', clue: 'Two peninsulas joined by a five-mile bridge, surrounded by four of the Great Lakes.' },
  { code: 'US-OH', clue: 'The Rock and Roll Hall of Fame stands on this state\'s Lake Erie shore.' },
  { code: 'US-GA', clue: 'Peaches, the Okefenokee Swamp and the southern end of the Appalachians mark this state.' },
  { code: 'US-LA', clue: 'A bayou state at the mouth of the Mississippi, where parishes replace counties.' },
  { code: 'US-PA', clue: 'The Liberty Bell and the Gettysburg battlefield sit in this keystone-shaped state.' },
  { code: 'US-UT', clue: 'The Great Salt Lake and five red-rock national parks belong to this state.' },
  { code: 'US-OR', clue: 'The deepest lake in the country fills a collapsed volcano in this Pacific state.' },
  { code: 'US-NM', clue: 'The first atomic bomb was tested in this high desert state of adobe and green chile.' },
  { code: 'US-MA', clue: 'Plymouth Rock, Cape Cod and Harvard belong to this New England state.' },
  { code: 'US-MN', clue: 'Ten thousand lakes and the headwaters of the Mississippi lie in this northern state.' },
  { code: 'US-WI', clue: 'Cheese, the Green Bay Packers and a long Lake Michigan shore define this state.' },
  { code: 'US-MO', clue: 'A stainless steel arch marks the gateway to the West where two great rivers meet.' },
  { code: 'US-TN', clue: 'The Great Smoky Mountains and the Grand Ole Opry belong to this long, narrow state.' },
  { code: 'US-NC', clue: 'The Wright brothers made their first flight above this state\'s Outer Banks.' },
  { code: 'US-VA', clue: 'Mount Vernon, Monticello and the Shenandoah Valley lie in this state.' },
  { code: 'US-NJ', clue: 'A boardwalk gambling resort and dense turnpike suburbs sit between two big-city neighbours.' },
  { code: 'US-ME', clue: 'Lobster boats, Acadia National Park and the country\'s easternmost point belong to this state.' },
  { code: 'US-MT', clue: 'Glacier National Park and the Little Bighorn battlefield lie in this big-sky state.' },
  { code: 'US-ID', clue: 'Potatoes, the Sawtooth range and a narrow panhandle reaching Canada mark this state.' },
  { code: 'US-WY', clue: 'Yellowstone\'s geysers and the Grand Teton range sit in this rectangular, least-populous state.' },
  { code: 'US-OK', clue: 'A panhandled state of Dust Bowl history, and where the Trail of Tears ended.' },
  { code: 'US-KS', clue: 'Tornado Alley wheat plains cover this state, which holds the centre of the contiguous forty-eight.' },
  { code: 'US-IA', clue: 'Corn, hogs and the first presidential caucuses belong to this state between two rivers.' },
  { code: 'US-IN', clue: 'Limestone quarries, basketball gyms and a short Lake Michigan shore mark this Midwestern state.' },
  { code: 'US-KY', clue: 'Bourbon, bluegrass and a horse race run on the first Saturday in May.' },
  { code: 'US-AL', clue: 'Rocket engineering at Huntsville and the Selma voting-rights marches belong to this Gulf state.' },
  { code: 'US-MS', clue: 'Blues music, cotton delta farmland and a short Gulf coast sit between Louisiana and Alabama.' },
  { code: 'US-AR', clue: 'The Ozark highlands and the only diamond mine in the country lie in this state.' },
  { code: 'US-SC', clue: 'The Civil War began at Fort Sumter, in this state\'s harbour.' },
  { code: 'US-MD', clue: 'Blue crabs, the Chesapeake Bay, and a state that wraps around the nation\'s capital.' },
  { code: 'US-CT', clue: 'Insurance, Yale University and a shoreline on Long Island Sound mark this small state.' },
  { code: 'US-WV', clue: 'Coal seams and the New River Gorge fill this mountain state, which split from its neighbour in 1863.' },
  { code: 'US-NE', clue: 'The Oregon Trail followed the Platte River across this plains state.' },
  { code: 'US-SD', clue: 'Four presidents are carved into a granite face in this state\'s Black Hills.' },
  { code: 'US-ND', clue: 'Badlands, an oil shale boom and the geographic centre of North America sit in this northern plains state.' },
  { code: 'US-NH', clue: 'The first presidential primary and the White Mountains belong to this small New England state.' },
  { code: 'US-VT', clue: 'Maple syrup and the Green Mountains fill this state on the eastern shore of Lake Champlain.' },
  { code: 'US-RI', clue: 'The smallest state, built around a bay of the same name and known for Gilded Age mansions.' },
  { code: 'US-DE', clue: 'The first state to ratify the Constitution, sharing its name with the bay on its eastern side.' },
  { code: 'US-PR', clue: 'El Yunque rainforest and a five-hundred-year-old Spanish fort guard this Caribbean territory.' },
];

/** Every subdivision the game may draw as a round, in fame order. */
export const SUBDIVISION_POOL: readonly string[] = CURATED_SUBDIVISIONS.map((s) => s.code);

/**
 * Pool sizes per tier, as PREFIXES of the fame order.
 *
 * 20 / 35 / 51. Twenty is the set an American schoolchild can place from the
 * shape alone; thirty-five reaches the plains states, where the outlines start
 * to rhyme; the full list is where the six-state north-east corner and Puerto
 * Rico live, and those are only findable if you have actually looked.
 */
export const SUBDIVISION_POOL_SIZE = {
  explorer: 20,
  navigator: 35,
  cartographer: CURATED_SUBDIVISIONS.length,
  // See `CITY_POOL_SIZE`: the country clue tier adds nothing to a pool that
  // is already clue-only and complete at Cartographer.
  surveyor: CURATED_SUBDIVISIONS.length,
} as const;
